/**
 * Export Service - Handle multiple export formats
 * SLAM Sports AI - Video Analysis System
 *
 * Supports: JSON, CSV, PDF, PPTX, DOCX, Google Drive, Email
 *
 * @module exportService
 */

import { parseReportToJSON, convertToCSV } from './reportParser';

/**
 * @typedef {import('./types').MediaItem} MediaItem
 * @typedef {import('./types').AnalysisExportJSON} AnalysisExportJSON
 * 
 * @typedef {Object} ExportJobPayload
 * @property {string} workspaceId - The target workspace ID to link the export to.
 * @property {string} templateId - The template identifier determining the Google Doc format.
 * @property {string} targetFolderId - The destination Google Drive folder ID.
 * @property {Object} context - Optional context variables, e.g., { sport: "basketball" }.
 * 
 * @typedef {Object} ReportExportData
 * @property {string} reportId - The SLAM report ID being exported.
 * @property {string} reportType - The type of report (e.g., "synergy_breakdown").
 * @property {Object} data - The normalized data payload for the chosen template.
 * @property {Array<{url: string, type: string, alt: string}>} charts - Snapshot charts to insert.
 */

/**
 * Extract a title from the analysis content for context-aware filenames
 * @param {string} reportContent - The markdown report content
 * @returns {string} A sanitized title for the filename
 */
export function extractTitleFromReport(reportContent) {
  if (!reportContent) return 'slam-analysis';

  // Try to find a title from the first heading
  const headingMatch = reportContent.match(/^#\s+(.+)$/m);
  if (headingMatch) {
    return sanitizeFilename(headingMatch[1]);
  }

  // Try to find "Analysis Report" or similar
  const reportMatch = reportContent.match(/([\w\s]+Analysis|Analysis[\w\s]+Report)/i);
  if (reportMatch) {
    return sanitizeFilename(reportMatch[1]);
  }

  // Try to extract from "Player:" field
  const playerMatch = reportContent.match(/\*\*Player[:\*]*\s*([^*\n]+)/i);
  if (playerMatch) {
    return sanitizeFilename(`${playerMatch[1]}-analysis`);
  }

  // Default fallback - sport-agnostic
  return 'slam-video-analysis';
}

/**
 * Sanitize string for use as filename
 * @param {string} str - Input string
 * @returns {string} Sanitized filename-safe string
 */
function sanitizeFilename(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-')          // Spaces to hyphens
    .replace(/-+/g, '-')           // Multiple hyphens to single
    .substring(0, 50)              // Limit length
    .trim();
}

/**
 * Build metadata object (sport-agnostic)
 * @param {MediaItem[]} mediaItems - Array of media items
 * @returns {Array} Processed media metadata
 */
function buildMediaMetadata(mediaItems) {
  return mediaItems.map((m) => ({
    name: m.name,
    type: m.type,
    metadata: m.metadata
      ? {
        analysisType: m.metadata.analysisType || m.metadata.pitchType, // Sport-agnostic
        cameraAngle: m.metadata.cameraAngle,
        shotType: m.metadata.shotType, // Basketball
        playType: m.metadata.playType, // General
      }
      : undefined,
  }));
}

/**
 * Export analysis report as JSON file
 * @param {string} reportContent - The markdown report content
 * @param {MediaItem[]} mediaItems - Array of media items
 * @param {string} [filename] - Optional filename for download
 * @returns {void}
 */
export function exportToJSON(reportContent, mediaItems, filename) {
  const jsonData = parseReportToJSON(reportContent, buildMediaMetadata(mediaItems));

  const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
    type: 'application/json',
  });
  const contextTitle = filename || extractTitleFromReport(reportContent);
  downloadBlob(blob, generateFilename(contextTitle, 'json'));
}

/**
 * Export analysis report as CSV file
 * @param {string} reportContent - The markdown report content
 * @param {MediaItem[]} mediaItems - Array of media items
 * @param {string} [filename] - Optional filename for download
 * @returns {void}
 */
export function exportToCSV(reportContent, mediaItems, filename) {
  const jsonData = parseReportToJSON(reportContent, buildMediaMetadata(mediaItems));

  const csvContent = convertToCSV(jsonData);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const contextTitle = filename || extractTitleFromReport(reportContent);
  downloadBlob(blob, generateFilename(contextTitle, 'csv'));
}

/**
 * Export analysis report as PDF
 * Uses browser print or html2canvas + jsPDF
 * @param {string} reportContent - The markdown report content
 * @param {string} [filename] - Optional filename for download
 * @returns {Promise<void>}
 */
export async function exportToPDF(reportContent, filename) {
  const contextTitle = filename || extractTitleFromReport(reportContent);

  // Create a styled HTML document for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to export PDF');
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${contextTitle}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { color: #1a1a2e; border-bottom: 2px solid #FFD700; padding-bottom: 10px; }
        h2 { color: #333; margin-top: 24px; }
        h3 { color: #555; }
        p { line-height: 1.6; }
        ul, ol { margin: 16px 0; }
        li { margin: 8px 0; }
        table { border-collapse: collapse; width: 100%; margin: 16px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #f5f5f5; }
        code { background: #f4f4f4; padding: 2px 6px; border-radius: 3px; }
        pre { background: #f4f4f4; padding: 16px; border-radius: 8px; overflow-x: auto; }
        .header { text-align: center; margin-bottom: 30px; }
        .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #888; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${contextTitle.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h1>
        <p style="color: #888;">Generated by SLAM Sports AI • ${new Date().toLocaleDateString()}</p>
      </div>
      <div class="content">${markdownToHTML(reportContent)}</div>
      <div class="footer">SLAM Sports AI - Video Analysis Platform</div>
    </body>
    </html>
  `);
  printWindow.document.close();

  // Give time for styles to load, then print
  setTimeout(() => {
    printWindow.print();
  }, 500);
}

/**
 * Simple markdown to HTML converter
 * @param {string} markdown - Markdown content
 * @returns {string} HTML content
 */
function markdownToHTML(markdown) {
  if (!markdown) return '';

  return markdown
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold and italic
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
    // Code blocks
    .replace(/```([^`]+)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Paragraphs
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(.+)$/gm, (match) => {
      if (match.startsWith('<')) return match;
      return `<p>${match}</p>`;
    });
}

/**
 * Export via email (opens mailto: link)
 * @param {string} reportContent - The markdown report content
 * @param {MediaItem[]} mediaItems - Array of media items
 * @returns {void}
 */
export function exportViaEmail(reportContent, mediaItems) {
  const contextTitle = extractTitleFromReport(reportContent);
  const subject = encodeURIComponent(`SLAM Analysis: ${contextTitle.replace(/-/g, ' ')}`);

  // Truncate body for mailto (has URL length limits)
  const bodyPreview = reportContent.substring(0, 1500);
  const body = encodeURIComponent(
    `${bodyPreview}\n\n---\nFull analysis available in SLAM Sports AI platform.`
  );

  window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
}

/**
 * Get parsed JSON data without downloading (for programmatic use)
 * @param {string} reportContent - The markdown report content
 * @param {MediaItem[]} mediaItems - Array of media items
 * @returns {AnalysisExportJSON} The parsed export data
 */
export function getExportData(reportContent, mediaItems) {
  return parseReportToJSON(reportContent, buildMediaMetadata(mediaItems));
}

/**
 * Download a blob as a file
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename for download
 * @returns {void}
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate timestamped filename
 * @param {string} prefix - Filename prefix
 * @param {string} extension - File extension
 * @returns {string} Generated filename
 */
export function generateFilename(prefix, extension) {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
  return `${prefix}-${dateStr}-${timeStr}.${extension}`;
}

/**
 * Copy JSON data to clipboard
 * @param {string} reportContent - The markdown report content
 * @param {MediaItem[]} mediaItems - Array of media items
 * @returns {Promise<boolean>} Whether copy was successful
 */
export async function copyJSONToClipboard(reportContent, mediaItems) {
  try {
    const jsonData = parseReportToJSON(reportContent, buildMediaMetadata(mediaItems));
    await navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Export analysis report as Markdown (.md) file
 * @param {string} reportContent - The markdown report content
 * @param {string} [filename] - Optional filename for download
 * @returns {void}
 */
export function exportToMarkdown(reportContent, filename) {
  if (!reportContent) return;
  const contextTitle = filename || extractTitleFromReport(reportContent);
  const header = `# ${contextTitle.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}\n\n> Generated by SLAM Sports AI • ${new Date().toLocaleDateString()}\n\n---\n\n`;
  const blob = new Blob([header + reportContent], { type: 'text/markdown;charset=utf-8;' });
  downloadBlob(blob, generateFilename(contextTitle, 'md'));
}

/**
 * Export analysis report as plain text (.txt) file
 * Strips markdown formatting for clean plaintext
 * @param {string} reportContent - The markdown report content
 * @param {string} [filename] - Optional filename for download
 * @returns {void}
 */
export function exportToTXT(reportContent, filename) {
  if (!reportContent) return;
  const contextTitle = filename || extractTitleFromReport(reportContent);

  const plaintext = reportContent
    // Convert headers to plain text with underlines
    .replace(/^### (.+)$/gm, (_, h) => `${h}\n${'-'.repeat(h.length)}`)
    .replace(/^## (.+)$/gm, (_, h) => `\n${h.toUpperCase()}\n${'='.repeat(h.length)}`)
    .replace(/^# (.+)$/gm, (_, h) => `${h.toUpperCase()}\n${'='.repeat(h.length)}`)
    // Strip bold/italic
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    // Strip inline code
    .replace(/`([^`]+)`/g, '$1')
    // Convert table separators to dashes
    .replace(/^\|[-:\s|]+\|$/gm, (m) => '-'.repeat(m.length))
    // Clean up table pipes
    .replace(/^\|(.+)\|$/gm, (_, content) => content.replace(/\|/g, '  |  ').trim())
    // Remove risk badges
    .replace(/\[(CRITICAL|HIGH|MEDIUM|LOW|NONE)\]/g, '$1');

  const header = `${contextTitle.replace(/-/g, ' ').toUpperCase()}\nGenerated by SLAM Sports AI - ${new Date().toLocaleDateString()}\n${'='.repeat(60)}\n\n`;
  const blob = new Blob([header + plaintext], { type: 'text/plain;charset=utf-8;' });
  downloadBlob(blob, generateFilename(contextTitle, 'txt'));
}

/**
 * Export analysis report as DOCX file
 * Uses dynamic import of 'docx' package for lazy loading
 * @param {string} reportContent - The markdown report content
 * @param {MediaItem[]} mediaItems - Array of media items
 * @param {string} [filename] - Optional filename for download
 * @returns {Promise<void>}
 */
export async function exportToDOCX(reportContent, mediaItems, filename) {
  if (!reportContent) return;
  const contextTitle = filename || extractTitleFromReport(reportContent);

  const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } = await import('docx');

  const lines = reportContent.split('\n');
  const docChildren = [];

  // Title page
  docChildren.push(
    new Paragraph({
      children: [new TextRun({ text: 'SLAM Sports AI', bold: true, size: 36, color: 'FFD700', font: 'Helvetica' })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    }),
    new Paragraph({
      children: [new TextRun({
        text: contextTitle.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        bold: true, size: 28, font: 'Helvetica',
      })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
    }),
    new Paragraph({
      children: [new TextRun({ text: `Generated ${new Date().toLocaleDateString()}`, italics: true, size: 20, color: '888888' })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    }),
    new Paragraph({
      border: { bottom: { style: BorderStyle.SINGLE, size: 3, color: 'FFD700' } },
      spacing: { after: 300 },
    })
  );

  // Parse markdown lines into DOCX paragraphs
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      docChildren.push(new Paragraph({ spacing: { after: 100 } }));
      continue;
    }

    // Headers
    const h1 = trimmed.match(/^# (.+)$/);
    const h2 = trimmed.match(/^## (.+)$/);
    const h3 = trimmed.match(/^### (.+)$/);

    if (h1) {
      docChildren.push(new Paragraph({ text: h1[1], heading: HeadingLevel.HEADING_1, spacing: { before: 300, after: 150 } }));
    } else if (h2) {
      docChildren.push(new Paragraph({ text: h2[1], heading: HeadingLevel.HEADING_2, spacing: { before: 240, after: 120 } }));
    } else if (h3) {
      docChildren.push(new Paragraph({ text: h3[1], heading: HeadingLevel.HEADING_3, spacing: { before: 200, after: 100 } }));
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      // Bullet list
      const text = trimmed.replace(/^[-*]\s/, '');
      docChildren.push(new Paragraph({
        children: parseInlineFormatting(TextRun, text),
        bullet: { level: 0 },
        spacing: { after: 60 },
      }));
    } else if (/^\d+\.\s/.test(trimmed)) {
      // Numbered list
      const text = trimmed.replace(/^\d+\.\s/, '');
      docChildren.push(new Paragraph({
        children: parseInlineFormatting(TextRun, text),
        numbering: { reference: 'default-numbering', level: 0 },
        spacing: { after: 60 },
      }));
    } else if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      // Table rows → render as tab-separated text
      if (/^\|[-:\s|]+\|$/.test(trimmed)) continue; // Skip separator row
      const cells = trimmed.split('|').filter(c => c.trim()).map(c => c.trim());
      docChildren.push(new Paragraph({
        children: [new TextRun({ text: cells.join('  |  '), size: 20, font: 'Courier New' })],
        spacing: { after: 40 },
      }));
    } else if (trimmed === '---' || trimmed === '***') {
      docChildren.push(new Paragraph({
        border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
        spacing: { after: 200 },
      }));
    } else {
      // Regular paragraph with inline formatting
      docChildren.push(new Paragraph({
        children: parseInlineFormatting(TextRun, trimmed),
        spacing: { after: 80 },
      }));
    }
  }

  // Footer
  docChildren.push(
    new Paragraph({ spacing: { before: 400 } }),
    new Paragraph({
      border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
      children: [new TextRun({ text: 'SLAM Sports AI - Video Analysis Platform', italics: true, size: 18, color: '888888' })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 200 },
    })
  );

  const doc = new Document({
    numbering: {
      config: [{
        reference: 'default-numbering',
        levels: [{ level: 0, format: 'decimal', text: '%1.', alignment: AlignmentType.START }],
      }],
    },
    sections: [{ children: docChildren }],
  });

  const buffer = await Packer.toBlob(doc);
  downloadBlob(buffer, generateFilename(contextTitle, 'docx'));
}

/**
 * Parse inline markdown formatting into TextRun objects for DOCX
 * @param {typeof import('docx').TextRun} TextRun - TextRun class from docx
 * @param {string} text - Text with inline markdown
 * @returns {import('docx').TextRun[]}
 */
function parseInlineFormatting(TextRun, text) {
  const runs = [];
  const regex = /\*\*([^*]+)\*\*|`([^`]+)`|\[([A-Z]+)\]/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      runs.push(new TextRun({ text: text.slice(lastIndex, match.index), size: 22 }));
    }
    if (match[1]) {
      // Bold
      runs.push(new TextRun({ text: match[1], bold: true, size: 22 }));
    } else if (match[2]) {
      // Code
      runs.push(new TextRun({ text: match[2], font: 'Courier New', size: 20, color: '996600' }));
    } else if (match[3]) {
      // Risk badge
      const colorMap = { CRITICAL: 'CC0000', HIGH: 'CC6600', MEDIUM: 'CC9900', LOW: '009900', NONE: '999999' };
      runs.push(new TextRun({ text: `[${match[3]}]`, bold: true, size: 20, color: colorMap[match[3]] || '000000' }));
    }
    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    runs.push(new TextRun({ text: text.slice(lastIndex), size: 22 }));
  }

  return runs.length > 0 ? runs : [new TextRun({ text, size: 22 })];
}

/**
 * Export options configuration for UI
 */
export const EXPORT_OPTIONS = [
  { id: 'pdf', name: 'PDF Document', icon: 'FileText', action: exportToPDF },
  { id: 'docx', name: 'DOCX Document', icon: 'FileText', action: exportToDOCX },
  { id: 'markdown', name: 'Markdown', icon: 'FileText', action: exportToMarkdown },
  { id: 'txt', name: 'Plain Text', icon: 'FileText', action: exportToTXT },
  { id: 'json', name: 'JSON Data', icon: 'FileJson', action: exportToJSON },
  { id: 'csv', name: 'CSV Spreadsheet', icon: 'Table', action: exportToCSV },
  { id: 'email', name: 'Send via Email', icon: 'Mail', action: exportViaEmail },
  { id: 'copy', name: 'Copy to Clipboard', icon: 'Copy', action: copyJSONToClipboard },
];
